/**
 * Step Data Loader
 *
 * Handles the `load` step property that allows pre-loading data
 * for use throughout the step lifecycle (intro, prompt, validate, outro)
 */

/**
 * Execute the load function and cache the result
 * @param {Object} step - The step configuration
 * @param {Object} sessionData - Current session data
 * @param {Object} callbacks - Available callbacks
 * @returns {Promise<Object>} Loaded data
 */
export async function executeStepLoader(step, sessionData, callbacks = {}) {
  if (!step.load || typeof step.load !== "function") {
    return null;
  }

  try {
    const loadedData = await step.load(sessionData, callbacks);
    return loadedData || {};
  } catch (error) {
    console.error("❌ Error in step load function:", error);
    throw new Error(`Step load failed: ${error.message}`);
  }
}

/**
 * Create an enhanced context object that includes loaded data
 * @param {Object} sessionData - Current session data
 * @param {Object} loadedData - Data from the load function
 * @param {Object} callbacks - Available callbacks
 * @returns {Object} Enhanced context for step functions
 */
export function createStepContext(
  sessionData,
  loadedData = null,
  callbacks = {}
) {
  const context = {
    ...sessionData,
    // Add loaded data under a special key to avoid conflicts
    $loaded: loadedData,
    $callbacks: callbacks,
  };

  // Add convenience methods for accessing loaded data
  context.$get = (key, defaultValue = undefined) => {
    return loadedData && loadedData[key] !== undefined
      ? loadedData[key]
      : defaultValue;
  };

  context.$has = (key) => {
    return loadedData && loadedData.hasOwnProperty(key);
  };

  context.$loadedKeys = () => {
    return loadedData ? Object.keys(loadedData) : [];
  };

  return context;
}

/**
 * Execute a step function with enhanced context that includes loaded data
 * @param {Function} stepFunction - The function to execute (intro, prompt, validate, etc.)
 * @param {any} inputValue - Input value (for functions that need it)
 * @param {Object} sessionData - Current session data
 * @param {Object} loadedData - Data from the load function
 * @param {Object} callbacks - Available callbacks
 * @returns {Promise<any>} Result of the step function
 */
export async function executeStepFunction(
  stepFunction,
  inputValue,
  sessionData,
  loadedData = null,
  callbacks = {}
) {
  // Handle simple string values (not functions)
  if (typeof stepFunction === "string") {
    return stepFunction;
  }

  if (!stepFunction || typeof stepFunction !== "function") {
    return null;
  }

  const context = createStepContext(sessionData, loadedData, callbacks);

  try {
    // Determine function signature and call appropriately
    const funcString = stepFunction.toString();
    const paramCount = stepFunction.length;

    console.log("🔍 executeStepFunction:", {
      paramCount,
      hasInput: inputValue !== null && inputValue !== undefined,
      callbacksType: typeof callbacks,
      callbacksKeys: callbacks ? Object.keys(callbacks) : "null",
      hasFire: callbacks?.fire ? "yes" : "no",
    });

    if (paramCount === 0) {
      return await stepFunction();
    } else if (paramCount === 1) {
      // Single parameter - could be inputValue, sessionData, or context
      if (
        funcString.includes("$loaded") ||
        funcString.includes("$get") ||
        funcString.includes("sessionData")
      ) {
        return await stepFunction(context);
      } else {
        return await stepFunction(
          inputValue !== null ? inputValue : sessionData
        );
      }
    } else if (paramCount === 2) {
      // Two parameters - sessionData and callbacks (for intro/prompt/outro)
      // OR inputValue and sessionData (for onStep/validate)
      if (inputValue === null || inputValue === undefined) {
        // No input - this is intro/prompt/outro: (sessionData, callbacks)
        console.log("📞 Calling with (sessionData, callbacks)");
        return await stepFunction(sessionData, callbacks);
      } else {
        // Has input - this is onStep/validate: (input, sessionData)
        return await stepFunction(inputValue, sessionData);
      }
    } else {
      // Three or more parameters: (input, sessionData, callbacks)
      return await stepFunction(inputValue, sessionData, callbacks);
    }
  } catch (error) {
    console.error("❌ Error executing step function:", error);
    throw error;
  }
}

/**
 * Check if a step has a load function
 * @param {Object} step - The step configuration
 * @returns {boolean} True if step has a load function
 */
export function hasStepLoader(step) {
  return step && typeof step.load === "function";
}

/**
 * Validate loaded data structure
 * @param {any} loadedData - Data returned from load function
 * @returns {boolean} True if data is valid
 */
export function validateLoadedData(loadedData) {
  // Loaded data should be an object or null
  return (
    loadedData === null ||
    loadedData === undefined ||
    (typeof loadedData === "object" && !Array.isArray(loadedData))
  );
}

/**
 * Create a debug summary of loaded data for logging
 * @param {Object} loadedData - Data from the load function
 * @returns {string} Debug summary
 */
export function createLoadedDataSummary(loadedData) {
  if (!loadedData) {
    return "No data loaded";
  }

  const keys = Object.keys(loadedData);
  const summary = keys
    .map((key) => {
      const value = loadedData[key];
      const type = typeof value;
      const preview =
        type === "object"
          ? Array.isArray(value)
            ? `Array(${value.length})`
            : "Object"
          : type === "string" && value.length > 20
          ? `"${value.substring(0, 20)}..."`
          : JSON.stringify(value);
      return `${key}: ${preview}`;
    })
    .join(", ");

  return `Loaded ${keys.length} properties: {${summary}}`;
}

export default {
  executeStepLoader,
  createStepContext,
  executeStepFunction,
  hasStepLoader,
  validateLoadedData,
  createLoadedDataSummary,
};
